import { graphql } from "gatsby"

import { PartialRecipe } from "./PartialRecipe"
import { ChefclubLocale } from "~/utils/locales"

// PartialPublicUser
export interface ApiPartialPublicUser {
  user_id: string
  username: string
  profile_picture: string | null
  user_types: string[]
  cover_image: string | null
  tags: Tag[]
  last_recipe_creation_date?: string | null
}

export interface PartialPublicUser {
  id: string
  username: string
  profilePicture: string | null
  userTypes: string[]
  coverImage?: string | null
  tags?: Tag[]
  lastRecipeCreationDate?: string | null
}

export const apiPartialPublicUserToPartialPublicUser = ({
  user_id,
  username,
  profile_picture,
  user_types,
  cover_image,
  tags,
  last_recipe_creation_date,
}: ApiPartialPublicUser): PartialPublicUser => ({
  id: user_id,
  username,
  profilePicture: profile_picture,
  userTypes: user_types,
  coverImage: cover_image,
  tags,
  lastRecipeCreationDate: last_recipe_creation_date,
})

export const partialPublicUserQuery = graphql`
  fragment PartialPublicUser on PartialPublicUser {
    id: user_id
    username
    profilePicture: profile_picture
    userTypes: user_types
    coverImage: cover_image
    tags
    lastRecipeCreationDate: last_recipe_creation_date
  }
`

// PublicUser
export interface ApiPublicUser extends ApiPartialPublicUser {
  biography: string
  followed_by_viewer: boolean
  followers_count: number
  follows_count: number
  instagram_profile: string | null
  posts_count: number
  authored_recipes_count: number
}

export interface PublicUser extends PartialPublicUser {
  biography: string
  followedByViewer: boolean
  followersCount: number
  followsCount: number
  instagramProfile: string | null
  postsCount: number
  authoredRecipeCount: number
}

export const apiPublicUserToPublicUser = ({
  user_id,
  username,
  cover_image,
  biography,
  followed_by_viewer,
  followers_count,
  follows_count,
  instagram_profile,
  posts_count,
  profile_picture,
  user_types,
  authored_recipes_count,
  tags,
  last_recipe_creation_date,
}: ApiPublicUser): PublicUser => ({
  id: user_id,
  username,
  biography,
  coverImage: cover_image,
  tags,
  lastRecipeCreationDate: last_recipe_creation_date,
  followedByViewer: followed_by_viewer,
  followersCount: followers_count,
  followsCount: follows_count,
  instagramProfile: instagram_profile,
  postsCount: posts_count,
  profilePicture: profile_picture,
  userTypes: user_types,
  authoredRecipeCount: authored_recipes_count,
})

export const apiCreatorsToPublicUser = ({
  authoredRecipesCount,
  biography,
  coverImageThumbnail,
  coverImage,
  followersCount,
  followsCount,
  instagramProfile,
  postsCount,
  profilePicture,
  userId,
  userTypes,
  username,
}: Creator): PublicUser => ({
  id: userId,
  username,
  biography,
  coverImage,
  tags: [],
  followedByViewer: false,
  followersCount,
  followsCount,
  instagramProfile,
  postsCount,
  profilePicture,
  userTypes,
  authoredRecipeCount: authoredRecipesCount,
})

export const publicUserQuery = graphql`
  fragment PublicUser on PublicUser {
    biography
    followedByViewer: followed_by_viewer
    followersCount: followers_count
    followsCount: follows_count
    instagramProfile: instagram_profile
    postsCount: posts_count
    authoredRecipeCount: authored_recipe_count
    coverImage: cover_image
    tags
    lastRecipeCreationDate: last_recipe_creation_date
  }
`

export type Tag = {
  slug: string
  translation: string
}

export interface Creator {
  authoredRecipesCount: number
  biography: string
  coverImageThumbnail: string
  coverImage: string
  followersCount: number
  followsCount: number
  instagramProfile: string
  postsCount: number
  profilePicture: string
  userId: string
  userTypes: string[]
  username: string
  recipes: PartialRecipe[]
  tags: Tag[]
  lang: ChefclubLocale
}

export interface PartialCreator {
  profilePicture: string
  username: string
}
